"use client";

import React, { useRef, useCallback, useState } from "react";
import { cn } from "@/lib/utils";

interface MfaCodeInputProps {
  value?: string;
  onChange: (value: string) => void;
  length?: number;
  disabled?: boolean;
  className?: string;
}

export function MfaCodeInput({
  value: externalValue = "",
  onChange,
  length = 6,
  disabled = false,
  className,
}: MfaCodeInputProps) {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  
  const [digits, setDigits] = useState<string[]>(() => {
    const initial = Array(length).fill("");
    externalValue.split("").forEach((char, i) => {
      if (i < length) initial[i] = char;
    });
    return initial;
  });

  const updateValue = useCallback(
    (newDigits: string[]) => {
      setDigits(newDigits);
      onChange(newDigits.filter(Boolean).join(""));
    },
    [onChange]
  );

  const setRef = useCallback(
    (index: number) => (el: HTMLInputElement | null) => {
      inputRefs.current[index] = el;
    },
    []
  );

  const focusInput = useCallback(
    (index: number) => {
      if (index >= 0 && index < length) {
        inputRefs.current[index]?.focus();
        inputRefs.current[index]?.select();
      }
    },
    [length]
  );

  const handleChange = useCallback(
    (index: number, inputValue: string) => {
      const digit = inputValue.replace(/\D/g, "");

      if (digit.length === 0) {
        return;
      }

      if (digit.length > 1) {
        const newDigits = [...digits];
        for (let i = 0; i < digit.length && index + i < length; i++) {
          newDigits[index + i] = digit[i];
        }
        updateValue(newDigits);
        const nextIndex = Math.min(index + digit.length, length - 1);
        setTimeout(() => focusInput(nextIndex), 0);
        return;
      }

      const newDigits = [...digits];
      newDigits[index] = digit;
      updateValue(newDigits);

      if (index < length - 1) {
        setTimeout(() => focusInput(index + 1), 0);
      }
    },
    [digits, length, updateValue, focusInput]
  );

  const handleKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      switch (e.key) {
        case "Backspace":
          e.preventDefault();
          if (digits[index]) {
            const newDigits = [...digits];
            newDigits[index] = "";
            updateValue(newDigits);
            setTimeout(() => focusInput(index), 0);
          } else if (index > 0) {
            const newDigits = [...digits];
            newDigits[index - 1] = "";
            updateValue(newDigits);
            setTimeout(() => focusInput(index - 1), 0);
          }
          break;

        case "Delete":
          e.preventDefault();
          if (digits[index]) {
            const newDigits = [...digits];
            newDigits[index] = "";
            updateValue(newDigits);
          }
          break;

        case "ArrowLeft":
          e.preventDefault();
          if (index > 0) {
            focusInput(index - 1);
          }
          break;

        case "ArrowRight":
          e.preventDefault();
          if (index < length - 1) {
            focusInput(index + 1);
          }
          break;

        case "Home":
          e.preventDefault();
          focusInput(0);
          break;

        case "End":
          e.preventDefault();
          focusInput(length - 1);
          break;

        default:
          if (/^\d$/.test(e.key) && digits[index] && index < length - 1) {
            const newDigits = [...digits];
            newDigits[index] = e.key;
            updateValue(newDigits);
            setTimeout(() => focusInput(index + 1), 0);
          }
          break;
      }
    },
    [digits, length, updateValue, focusInput]
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      e.preventDefault();
      const pastedData = e.clipboardData
        .getData("text")
        .replace(/\D/g, "")
        .slice(0, length);

      if (pastedData.length > 0) {
        const newDigits = Array(length).fill("");
        pastedData.split("").forEach((char, i) => {
          if (i < length) newDigits[i] = char;
        });
        updateValue(newDigits);
        const focusIndex = Math.min(pastedData.length, length - 1);
        setTimeout(() => focusInput(focusIndex), 0);
      }
    },
    [length, updateValue, focusInput]
  );

  const handleFocus = useCallback(
    (index: number) => {
      if (digits[index]) {
        inputRefs.current[index]?.select();
      }
    },
    [digits]
  );

  return (
    <div className={cn("flex justify-center gap-2", className)}>
      {Array.from({ length }).map((_, index) => (
        <input
          key={index}
          ref={setRef(index)}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={1}
          disabled={disabled}
          className={cn(
            "w-12 h-14 text-center text-2xl font-semibold border rounded-lg",
            "focus:ring-2 focus:ring-primary focus:border-primary outline-none",
            "transition-all duration-150",
            disabled && "opacity-50 cursor-not-allowed",
            digits[index] && "border-primary bg-primary/5"
          )}
          value={digits[index] || ""}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          onFocus={() => handleFocus(index)}
        />
      ))}
    </div>
  );
}
